import {
  addDays,
  differenceInCalendarDays,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import { getScheduleEventCompositeKey } from '~/helpers/scheduleEventKey';
import type { CalendarEventWithClip, PlaceData } from './types';

export function buildCalendarWeekEvents(
  calendar: PlaceData['calendar'] | undefined,
  siteId: string,
  weekStart: Date,
  bookingsHidden: boolean
): CalendarEventWithClip[] {
  if (!calendar) return [];

  const weekStartDay = startOfDay(weekStart);
  const weekEndDay = startOfDay(addDays(weekStart, 6));
  const weekEvents: CalendarEventWithClip[] = [];

  for (let eventIndex = 0; eventIndex < calendar.length; eventIndex++) {
    const job = calendar[eventIndex];
    if (!job.start) continue;
    if (job.status === 'Booking' && bookingsHidden) continue;

    const jobStartDay = startOfDay(new Date(job.start));
    const jobEndDay = job.end ? startOfDay(new Date(job.end)) : jobStartDay;

    // For bookings, the end date is typically the checkout day (the day the guest leaves).
    // effectiveEndDay = last occupied night, used ONLY for:
    //   1. Skip check (determine if booking has nights in this week)
    //   2. Lane calculation (back-to-back bookings share lanes)
    // The original checkout day (jobEndDay) is used for visual display and clip detection.
    const isBooking = job.status === 'Booking';
    const effectiveEndDay =
      isBooking && jobEndDay > jobStartDay
        ? addDays(jobEndDay, -1) // Last occupied night (not checkout day)
        : jobEndDay;

    // Skip if no overlap with this week
    if (effectiveEndDay < weekStartDay || jobStartDay > weekEndDay) continue;

    // Clamp to week boundaries
    const clampedStart =
      jobStartDay < weekStartDay ? weekStartDay : jobStartDay;
    // For DISPLAY, use the original checkout date (jobEndDay) so pills visually end on checkout day.
    // effectiveEndDay is only used for overlap/clip/lane calculations.
    const clampedEnd = jobEndDay > weekEndDay ? weekEndDay : jobEndDay;

    const columnIndex = Math.max(
      0,
      differenceInCalendarDays(clampedStart, weekStartDay)
    );

    // Clip detection: use original checkout date (jobEndDay) for bookings
    // so cross-week bookings are visually clipped correctly.
    const clip = {
      left: jobStartDay < weekStartDay,
      right: jobEndDay > weekEndDay,
    };

    weekEvents.push({
      y: 0,
      x: columnIndex,
      event: {
        id: job.id,
        status: job.status,
        assignee: job.assignee,
        included: null,
        attributes: job.attributes,
        tags: job.tags ?? null,
        summary: job.summary,
        start: clampedStart.toISOString(),
        end: clampedEnd.toISOString(),
        scheduledStartTime: job.scheduledStartTime,
        scheduledEndTime: job.scheduledEndTime,
        timeZone: job.timeZone,
        filtered: job.filtered ?? false,
      },
      clip,
      eventIndex,
      siteId,
      compositeKey: getScheduleEventCompositeKey(job, siteId),
    } as CalendarEventWithClip);
  }

  return weekEvents;
}

/**
 * Get the week start dates that are currently visible in the virtualizer
 * This should be called from the parent to determine which weeks need positions
 */
export const getVisibleWeekStarts = (
  weeks: Date[],
  virtualItems: Array<{ index: number }>,
  overscan = 2
): Date[] => {
  if (virtualItems.length === 0) return [];

  const minIndex = Math.max(0, virtualItems[0].index - overscan);
  const maxIndex = Math.min(
    weeks.length - 1,
    virtualItems[virtualItems.length - 1].index + overscan
  );

  const result: Date[] = [];
  for (let i = minIndex; i <= maxIndex; i++) {
    result.push(startOfWeek(weeks[i], { weekStartsOn: 1 }));
  }

  return result;
};
